"use client";
/* eslint-disable react/no-array-index-key */
import { useDispatch, useSelector } from "react-redux";
import { Box, Text } from "@quintoandar-tokko/jopijs";
import DescriptionBox from "../../descriptionBox/DescriptionBox";
import style from "./basicInformation.module.scss";
import translations from "../FichaInfo.trans.json";
import Trans from "@/components/shared/Trans";
import ListService from "./ListService";
import { setEditedData } from "@/store/slices/components/fichaInfo/FichaInfoSlice";
import Skeletons from "../skelenton/Skeleton";
import WysiwygEditor from "@/components/WysiwygEditor";
import { debounce, getAllContents } from "../utilities/helpers";
import { useEffect, useState } from "react";
import { chunkArray } from "@/components/shared/Hooks";

export default function BasicInformation() {
  const dispatch = useDispatch();
  const state = useSelector((ste) => ste.FichaInfoSlice);
  const { isEdition, showData, edited_data, origin_data, loading } = state;
  const data = state[showData];
  const { has_edition, description, development } = edited_data;
  const { basic_info, measurement } = state.origin_data;
  const [resize, setResize] = useState();
  const show_description = getAllContents(data.description)[0] || isEdition;

  const show_development_description =
    (data.development?.description &&
      getAllContents(data.development.description)[0]) ||
    (origin_data.development && isEdition);

  const basicInfo = basic_info ? chunkArray(basic_info, 3) : [];

  const measurementInfo =
    measurement?.length > 0 ? chunkArray(measurement, 3) : [];

  const handleTextAreaChange = (e, key) => {
    dispatch(setEditedData({ key, value: e }));
  };

  useEffect(() => {
    const changeWidth = () => {
      setResize(window.innerWidth);
    };
    const debouncedHandleResize = debounce(changeWidth, 500);
    window.addEventListener("resize", debouncedHandleResize);
    return () => window.removeEventListener("resize", debouncedHandleResize);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      setResize(window.innerWidth);
    }, 1);
  }, []);

  if (loading)
    return (
      <>
        <Box className={style.basicInformationCont}>
          <Box className={style.allSkeletomItemsContainer}>
            <Skeletons width={150} borderRadius={50} />
            <Box className={style.itemsSkeletonContainer}>
              <Box className={style.BIItems}>
                {Array(3)
                  .fill()
                  .map((item, index) => (
                    <Skeletons key={`${item}+${index}`} borderRadius={50} />
                  ))}
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className={style.descriptionSkeletonContainer}>
          <Skeletons width={100} borderRadius={50} />
          <Skeletons width="100%" height={100} borderRadius={16} />
        </Box>
        <Box className={style.aditionalSkeletonContainer}>
          {Array(2)
            .fill()
            .map((col, index) => (
              <Box
                key={`${col}+${index}`}
                className={style.allSkeletomItemsContainer}
              >
                <Skeletons width={150} borderRadius={50} />
                <Box className={style.itemsSkeletonContainer}>
                  <Box className={style.BIItems}>
                    {Array(3)
                      .fill()
                      .map((item2, index) => (
                        <Skeletons
                          key={`${item2}+${index}`}
                          borderRadius={50}
                        />
                      ))}
                  </Box>
                </Box>
              </Box>
            ))}
        </Box>
      </>
    );

  return (
    <Box className={style.basicInformationCont}>
      {basicInfo.length > 0 && (
        <Box className={`${style.bItem}  ${isEdition ? style.opacity : ""}`}>
          <Text className={style.BITitle} variant="bodyBold.fontSize14">
            {Trans(translations, "BasicInformation")}
          </Text>
          <Box className={style.infoCont}>
            {basicInfo.map((column, columnIndex) => (
              <Box key={columnIndex + 1} className={style.BIItems}>
                {column.map(
                  (item, index) =>
                    item.name && (
                      <Text key={index} variant="body.fontSize14">
                        {item.name}: {item.value}
                      </Text>
                    )
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {measurementInfo.length > 0 && (
        <Box className={`${style.bItem}  ${isEdition ? style.opacity : ""}`}>
          <Text className={style.BITitle} variant="bodyBold.fontSize14">
            {Trans(translations, "SurfacesAndMeasurements")}
          </Text>
          <Box className={style.infoCont}>
            {measurementInfo.map((column, columnIndex) => (
              <Box key={columnIndex + 1} className={style.BIItems}>
                {column.map(
                  (item, index) =>
                    item.name && (
                      <Text key={index} variant="body.fontSize14">
                        {item.name}: {item.value}
                      </Text>
                    )
                )}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      {show_description && (
        <Box className={style.bItem}>
          <Text className={style.BITitle} variant="bodyBold.fontSize14">
            {Trans(translations, "Description")}
          </Text>
          {!isEdition ? (
            <DescriptionBox text={data.description} key={resize + showData} />
          ) : (
            <Box className={style.textAreaDescriptionContainer}>
              <WysiwygEditor
                onChange={(e) => handleTextAreaChange(e, "description")}
                defaultContent={
                  !has_edition ? origin_data.description : description
                }
              >
                <WysiwygEditor.Toolbar />
                <WysiwygEditor.Editor />
              </WysiwygEditor>
            </Box>
          )}
        </Box>
      )}
      {show_development_description && (
        <Box className={style.bItem}>
          <Text className={style.BITitle} variant="bodyBold.fontSize14">
            {Trans(translations, "DevelopmentDescription")}
          </Text>
          {!isEdition ? (
            <DescriptionBox
              text={data.development.description}
              key={resize + showData}
              dependency={data.development.description}
            />
          ) : (
            <Box className={style.textAreaDescriptionContainer}>
              <WysiwygEditor
                onChange={(e) => handleTextAreaChange(e, "development")}
                defaultContent={
                  !has_edition
                    ? origin_data.development.description
                    : development.description
                }
              >
                <WysiwygEditor.Toolbar />
                <WysiwygEditor.Editor />
              </WysiwygEditor>
            </Box>
          )}
        </Box>
      )}
      <Box
        className={`${style.servAllContainers} ${
          isEdition ? style.opacity : ""
        }`}
      >
        {state.origin_data.services.length > 0 && (
          <ListService title="Services" array={state.origin_data.services} />
        )}

        {state.origin_data.rooms.length > 0 && (
          <ListService title="Rooms" array={state.origin_data.rooms} />
        )}

        {state.origin_data.additional?.length > 0 && (
          <ListService
            title="Additionals"
            array={state.origin_data.additional}
          />
        )}
      </Box>
    </Box>
  );
}
